import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { FirebaseAuthProvider } from '@shared/modules/auth/providers/firebase';
import { UserService } from '@shared/modules/auth/services/user.service';
import { ParametryService } from '@pages/parametry/parametry.service';
import { planKey } from '@pages/parametry/pricing.config';
import { catchError, filter, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import { AdministrationUser, getConnectionWsp, WspStatus } from 'src/app/models/user';
import { environment } from 'src/environments/environment';

/* si parametry/global no tiene wspStatusCheckMinutes: cada cuántos minutos, como mínimo, se consulta el estado en YCloud */
const DEFAULT_CHECK_MINUTES = 15;
/* al enviar o si falla un envío se vuelve a consultar aunque no hayan pasado los 15 minutos, pero no más de una vez por minuto */
const MIN_FORCED_CHECK_MS = 60 * 1000;
/* cada cuánto se fija (sin consultar) si el estado guardado ya venció, mientras la pestaña está a la vista */
const TICK_MS = 60 * 1000;

/* status que devuelve YCloud cuando el número está conectado. Cualquier otro valor es "desconectado" */
const CONNECTED = 'CONNECTED';
/* se guarda cuando n8n responde 404: el número no está dado de alta en YCloud */
export const WSP_NOT_FOUND = 'NOT_FOUND';

/*
 * 'connected':      conectado, o todavía sin dato (no se bloquea a nadie por falta de dato)
 * 'disconnected':   YCloud dice que no está conectado (cualquier status distinto de CONNECTED, incluido NOT_FOUND)
 * 'not-configured': la cuenta no tiene WhatsApp configurado (sin número válido o connectionWsp 'none'); no se consulta
 * 'no-subscription': sin suscripción activa; no se consulta
 * 'none':           no es una administración (o todavía no cargó la cuenta)
 */
export type WspConnectionState = 'connected' | 'disconnected' | 'not-configured' | 'no-subscription' | 'none';

/* texto del aviso y del mensaje del botón */
export const WSP_DISCONNECTED_TEXT = 'Tu WhatsApp está desconectado. Mientras tanto, AdminAI no puede recibir ni mandar mensajes. Comunicate con Soporte de AdminAI para volver a conectarlo.';
export const WSP_SEND_BLOCKED_TEXT = 'No se pudo enviar: tu WhatsApp está desconectado.';

/*
 * Número de WhatsApp de la administración en el formato de la consulta de n8n ("+549...") o undefined si no hay uno válido.
 * Es administrationWsp, el mismo con el que se envía y se leen los chats. Se saca lo que no sea número adelante
 * (en test hay cuentas con "undefined" pegado, L60).
 */
export function wspPhoneNumber(user: Partial<AdministrationUser> | null | undefined): string | undefined {
    const digits = String(user?.administrationWsp ?? '').replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15 ? '+' + digits : undefined;
}

/*
 * Normaliza el número del agente que se carga en el perfil (AF327): solo dígitos, con "+" adelante. No agrega ni saca
 * el 9 de los celulares de Argentina: se carga tal como figura en WhatsApp. undefined si no tiene entre 10 y 15 dígitos.
 */
export function normalizeAgentNumber(value: string | null | undefined): string | undefined {
    const digits = String(value ?? '').replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 15 ? '+' + digits : undefined;
}

/*
 * Estado real de la conexión de WhatsApp de la administración (AF327).
 * - Se guarda en la cuenta (users/{parentUid}/accounts/administration.wspStatus) y se escucha en vivo: las otras pestañas
 *   y usuarios de la misma administración lo ven sin volver a consultar.
 * - Se consulta a n8n (estado-conexion) solo si el estado guardado tiene más de wspStatusCheckMinutes (15): al entrar,
 *   al cambiar de pantalla y cada minuto se fija si venció, solo con la pestaña a la vista.
 * - Al enviar (ensureCanSend) o si falla un envío (onSendFailed) se vuelve a consultar en el momento.
 * - Un error de la consulta (502, sin respuesta) no cambia el estado guardado.
 */
@Injectable({
    providedIn: 'root',
})
export class WspStatusService {
    private http = inject(HttpClient);
    private router = inject(Router);
    private authProvider = inject(FirebaseAuthProvider);
    private userService = inject(UserService);
    private parametryService = inject(ParametryService);

    private loggedUser = toSignal(this.authProvider.getLoggedUser() as unknown as Observable<AdministrationUser | null>);
    private parametry = toSignal(this.parametryService.getOne('global').pipe(catchError(() => of(null))));
    /* cuenta de la administración (la del parentUid: es la misma para todos sus usuarios), en vivo */
    private account = toSignal(
        toObservable(computed(() => this.loggedUser()?.type === 'administration' ? this.loggedUser()?.parentUid : undefined)).pipe(
            switchMap(parentUid => parentUid
                ? (this.userService.getAccount(parentUid, 'administration') as Observable<AdministrationUser>).pipe(catchError(() => of(null)))
                : of(null))
        )
    );

    //se crea acá: toObservable solo se puede usar al construir el service (no dentro de start)
    private account$ = toObservable(this.account);

    private checking = false;
    private lastForcedCheck = 0;
    private started = false;
    /* para que la pantalla muestre "verificando" si quiere */
    checkingNow = signal(false);

    private checkMinutes = computed(() => {
        const value = Number(this.parametry()?.wspStatusCheckMinutes);
        return value > 0 ? value : DEFAULT_CHECK_MINUTES;
    });

    wspStatus = computed<WspStatus | undefined>(() => this.account()?.wspStatus ?? undefined);

    state = computed<WspConnectionState>(() => {
        const user = this.account();
        if (!user || user.type !== 'administration') return 'none';
        if (!planKey(user.subscription)) return 'no-subscription';
        if (getConnectionWsp(user) === 'none' || !wspPhoneNumber(user)) return 'not-configured';
        const status = this.wspStatus()?.status;
        //sin dato todavía: se muestra conectado, como antes de AF327
        if (!status) return 'connected';
        return status === CONNECTED ? 'connected' : 'disconnected';
    });

    /* motivo para mostrar en el aviso y en el mensaje del botón */
    reason = computed<string>(() => {
        switch (this.state()) {
            case 'not-configured': return 'No hay una cuenta de WhatsApp configurada.';
            case 'disconnected': return this.wspStatus()?.status === WSP_NOT_FOUND
                ? 'El número configurado no está dado de alta en WhatsApp.'
                : WSP_DISCONNECTED_TEXT;
            default: return '';
        }
    });

    /* desconectado o sin configurar: se muestra el aviso fijo y se bloquean los envíos (no para las cuentas sin suscripción) */
    isDisconnected = computed(() => this.state() === 'disconnected' || this.state() === 'not-configured');

    /*
     * Arranca las revisiones automáticas (una sola vez, la llama la topbar): al entrar, al cambiar de pantalla,
     * al volver a la pestaña y cada minuto con la pestaña a la vista. Cada una consulta solo si el estado venció.
     */
    start(destroyRef?: DestroyRef) {
        if (this.started) return;
        this.started = true;
        //primera revisión cuando la cuenta ya cargó
        firstValueFrom(this.account$.pipe(filter(Boolean))).then(() => this.check());
        const nav = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.check());
        const onVisible = () => { if (document.visibilityState === 'visible') this.check(); };
        document.addEventListener('visibilitychange', onVisible);
        const tick = setInterval(() => this.check(), TICK_MS);
        destroyRef?.onDestroy(() => {
            nav.unsubscribe();
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(tick);
            this.started = false;
        });
    }

    /*
     * Consulta el estado en n8n si corresponde y lo guarda en la cuenta.
     * force: sin esperar los 15 minutos (al enviar o si falló un envío), con un mínimo de 1 minuto entre consultas forzadas.
     */
    async check(force = false): Promise<void> {
        const user = this.account();
        if (!user || this.checking) return;
        //sin suscripción o sin WhatsApp configurado no se consulta
        if (this.state() === 'no-subscription' || this.state() === 'not-configured' || this.state() === 'none') return;
        //con la pestaña oculta no se consulta (al volver se revisa por visibilitychange)
        if (document.visibilityState === 'hidden') return;

        const now = Date.now();
        if (force) {
            if (now - this.lastForcedCheck < MIN_FORCED_CHECK_MS) return;
        } else {
            const checkedAt = toDate(this.wspStatus()?.checkedAt);
            if (checkedAt && now - checkedAt.getTime() < this.checkMinutes() * 60 * 1000) return;
        }

        const phoneNumber = wspPhoneNumber(user);
        const parentUid = user.parentUid ?? this.loggedUser()?.parentUid;
        if (!phoneNumber || !parentUid) return;

        this.checking = true;
        this.checkingNow.set(true);
        if (force) this.lastForcedCheck = now;
        try {
            let status: string | undefined;
            try {
                const response = await firstValueFrom(this.http.post<{ success: boolean; status?: string }>(
                    environment.agentePath + 'estado-conexion',
                    { phoneNumber },
                    { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }
                ));
                status = response?.success && response.status ? String(response.status) : undefined;
            } catch (error) {
                //404: el número no está en YCloud (se guarda). 400, 502 o sin respuesta: no se toca el estado guardado
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    status = WSP_NOT_FOUND;
                } else {
                    console.warn('[estado WhatsApp] no se pudo consultar', error);
                }
            }
            if (status) {
                const wspStatus: WspStatus = { provider: 'ycloud', status, checkedAt: new Date() };
                await this.userService.update(parentUid, { wspStatus }, 'administration');
            }
        } catch (error) {
            console.warn('[estado WhatsApp] no se pudo guardar', error);
        } finally {
            this.checking = false;
            this.checkingNow.set(false);
        }
    }

    /*
     * Antes de mandar un mensaje de WhatsApp: si el estado guardado es desconectado, vuelve a consultar en el momento
     * (por si se reconectó) y devuelve si se puede enviar. Sin dato o conectado: true.
     */
    async ensureCanSend(): Promise<boolean> {
        if (this.state() === 'disconnected') {
            await this.check(true);
        }
        return !this.isDisconnected();
    }

    /* falló un envío: puede ser que se haya desconectado entre revisiones, se consulta en el momento */
    onSendFailed() {
        this.check(true);
    }

    /*
     * Después de cambiar el número del agente en el perfil: espera a que la cuenta (en vivo) traiga el número nuevo
     * y consulta en el momento, sin esperar los 15 minutos ni el mínimo entre consultas forzadas
     */
    async checkAfterNumberChange(newNumber: string) {
        for (let i = 0; i < 20 && wspPhoneNumber(this.account()) !== newNumber; i++) {
            await new Promise(r => setTimeout(r, 250));
        }
        this.lastForcedCheck = 0;
        await this.check(true);
    }
}

function toDate(value: any): Date | undefined {
    if (!value) return undefined;
    const date = value?.toDate ? value.toDate() : new Date(value);
    return isNaN(date.getTime()) ? undefined : date;
}
